<template>
    <div>
        <canvas ref="radarChartCanvas"></canvas>
    </div>
</template>

<script>
import { Chart, registerables } from 'chart.js';
Chart.register(...registerables);

export default {
    name: 'ConfectionProductionReportingRadarGraphicData',
    props: {
        referencesData: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            chart: null,
            metrics: [
                { key: 'workedTimeWithOR', label: 'Tiempo trabajado con órdenes', type: 'time', suffix: '' },
                { key: 'optimalTime', label: 'Tiempo óptimo', type: 'time', suffix: '' },
                { key: 'worthTimePercentage', label: 'Porcentaje de tiempo aprovechado', type: 'percentage', suffix: ' %' },
                { key: 'ORFinishedQuantity', label: 'Órdenes finalizadas', type: 'orders', suffix: ' órdenes finalizadas' },
                { key: 'averageFinishedBoxesRatePerHour', label: 'Cajas finalizadas por hora', type: 'boxesProduction', suffix: ' c/h' },
                { key: 'boxesProducedQuantity', label: 'Cajas producidas', type: 'boxes', suffix: ' cajas' },
                { key: 'bagsProducedQuantity', label: 'Bolsas producidas', type: 'bags', suffix: ' bolsas' },
                { key: 'bagsUsedInBoxesQuantity', label: 'Bolsas usadas', type: 'bags', suffix: ' bolsas' },
                { key: 'qualityRate', label: 'Calidad', type: 'percentage', suffix: ' %' },
                { key: 'weightAccumulated', label: 'Peso acumulado (kg)', type: 'weight', suffix: ' kg' },
                { key: 'weightExcess', label: 'Exceso de peso (kg)', type: 'weight', suffix: ' kg' },
            ]
        };
    },
    computed: {
        metricKeys() {
            return this.metrics.map(m => m.key);
        },
        radarLabels() {
            return this.metrics.map(m => m.label);
        },
        // Calcula el máximo para cada tipo de métrica
        groupMaxValues() {
            const groupMax = {};
            const dataLines = Object.values(this.referencesData);
            this.metrics.forEach(metric => {
                const group = metric.type;
                if (!groupMax[group]) groupMax[group] = 0;
                dataLines.forEach(line => {
                    const val = line.summedData[metric.key] || 0;
                    if (metric.type === 'percentage') {
                        groupMax[group] = 100; // El porcentaje siempre se normaliza a 100
                    } else {
                        groupMax[group] = Math.max(groupMax[group], val);
                    }
                });
            });
            return groupMax;
        },
        datasets() {
            const lines = Object.values(this.referencesData);
            if (!lines.length) return [];

            return lines.map(line => {
                // Datos crudos de cada métrica
                const raw = this.metricKeys.map(key => line.summedData[key] || 0);
                // Normaliza cada valor según el máximo de su grupo
                const normalized = raw.map((v, i) => {
                    const metric = this.metrics[i];
                    const max = this.groupMaxValues[metric.type] || 1;
                    return max > 0 ? v / max : 0;
                });
                return {
                    label: line.name,
                    data: normalized,
                    originalData: raw,
                    ...(lines.length > 7 && {
                        borderColor: line.color,
                        backgroundColor: line.color.replace('rgb(', 'rgba(').replace(')', ', 0.5)'),
                        pointBackgroundColor: line.color,
                        pointBorderColor: line.color,
                        pointHoverBackgroundColor: '#000',
                        pointHoverBorderColor: '#000',
                    }),
                    borderWidth: 2
                };
            });
        }
    },
    methods: {
        createChart() {
            const ctx = this.$refs.radarChartCanvas.getContext('2d');
            if (this.chart) this.chart.destroy();

            this.chart = new Chart(ctx, {
                type: 'radar',
                data: {
                    labels: this.radarLabels,
                    datasets: this.datasets
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 10, bottom: 10 } },
                    scales: {
                        r: {
                            beginAtZero: true,
                            max: 1,
                            ticks: { display: false }
                        }
                    },
                    plugins: {
                        legend: { position: 'top', labels: { padding: 10 } },
                        tooltip: {
                            callbacks: {
                                label: context => {
                                    const ds = context.dataset;
                                    const idx = context.dataIndex;
                                    const metric = this.metrics[idx];
                                    const rawVal = ds.originalData[idx] ?? 0;
                                    return `${ds.label}: ${this.prepareLabel(rawVal, metric)}`;
                                }
                            }
                        }
                    }
                }
            });
        },
        prepareLabel(value, metric) {
            let formattedValue = value;

            if (metric.type === 'time') {
                formattedValue = this.formatSecondsToTime(value);
            } else if (typeof formattedValue === 'number') {
                formattedValue = new Intl.NumberFormat('es-ES', { useGrouping: true }).format(formattedValue);
            }

            if (metric.type === 'percentage') {
                const numericValue = parseFloat(formattedValue);
                if (!isNaN(numericValue)) {
                    formattedValue = Math.min(numericValue, 100);
                }
            }

            return `${formattedValue}${metric.suffix}`;
        },
        formatSecondsToTime(seconds) {
            if (isNaN(seconds) || seconds < 0) return '0s';
            const units = [
                { label: 'a', value: 31556926 },
                { label: 'mo', value: 2629743 },
                { label: 'w', value: 604800 },
                { label: 'd', value: 86400 },
                { label: 'h', value: 3600 },
                { label: 'm', value: 60 },
                { label: 's', value: 1 }
            ];
            let rem = Math.floor(seconds);
            const parts = [];
            for (const u of units) {
                const cnt = Math.floor(rem / u.value);
                if (cnt > 0 || (u.label === 's' && parts.length === 0)) {
                    parts.push(`${cnt}${u.label}`);
                    rem -= cnt * u.value;
                }
            }
            return parts.join(' ');
        }
    },
    watch: {
        referencesData: {
            handler() { this.createChart(); },
            deep: true
        }
    },
    mounted() {
        this.createChart();
    },
    beforeUnmount() {
        if (this.chart) this.chart.destroy();
    }
};
</script>
